import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, ArrowRight, Trophy, Sparkle, Clock, ChatCircleDots } from '@phosphor-icons/react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * Lesson reader modal.
 * Shows overview → content → quiz → completion screen.
 * Calls /api/learn/v2/complete on submit to award XP.
 */
const LessonReaderModal = ({ lesson, onClose, onComplete, onAskTutor }) => {
  const [phase, setPhase] = useState('reading'); // reading | quiz | done
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ESC closes the modal
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!lesson) return null;
  const quiz = lesson.quiz || [];
  const correctCount = quiz.reduce((n, q, i) => n + (answers[i] === q.correct ? 1 : 0), 0);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post('/learn/v2/complete', {
        lesson_id: lesson.id,
        quiz_correct: correctCount,
      });
      setResult(res.data);
      setPhase('done');
      if (res.data.xp_gained > 0) {
        toast.success(`+${res.data.xp_gained} XP earned!`, { duration: 3000 });
      }
      onComplete && onComplete(res.data);
    } catch (e) {
      toast.error('Could not save progress. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm overflow-y-auto p-4 md:p-10"
      onClick={onClose}
      data-testid="lesson-reader-modal"
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="max-w-2xl mx-auto bg-zinc-950 border border-white/10 rounded-2xl overflow-hidden my-6"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-white/10 bg-zinc-950/95 backdrop-blur">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{lesson.emoji}</span>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gold">{lesson.category}</p>
              <h2 className="text-white font-unbounded font-bold text-lg leading-tight">{lesson.title}</h2>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1" data-testid="lesson-close">
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 md:p-8">
          {phase === 'reading' && (
            <div className="space-y-6">
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-300 capitalize">
                  {lesson.level}
                </span>
                <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-300">
                  <Clock size={12} /> {lesson.reading_minutes} min read
                </span>
                <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold">
                  <Sparkle size={12} weight="fill" /> {lesson.xp_reward} XP
                </span>
              </div>

              <p className="text-lg text-zinc-200 leading-relaxed border-l-2 border-gold pl-4">
                {lesson.overview}
              </p>

              <div className="space-y-4 text-zinc-300 leading-relaxed" data-testid="lesson-content">
                {lesson.content.map((block, i) => (
                  <p key={i} dangerouslySetInnerHTML={{
                    __html: block.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
                  }} />
                ))}
              </div>

              {lesson.key_takeaways?.length > 0 && (
                <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-4">
                  <p className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Key Takeaways</p>
                  <ul className="space-y-1.5">
                    {lesson.key_takeaways.map((t, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                        <CheckCircle size={16} weight="fill" className="text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setPhase('quiz')}
                  className="flex-1 min-w-[200px] px-5 py-3 rounded-full bg-gradient-to-r from-yellow-300 to-yellow-500 text-black font-bold text-sm hover:scale-[1.02] transition-transform flex items-center justify-center gap-2"
                  data-testid="lesson-start-quiz"
                >
                  Start quiz <ArrowRight size={16} weight="bold" />
                </button>
                <button
                  onClick={() => onAskTutor && onAskTutor(lesson.id)}
                  className="px-4 py-3 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-sm hover:bg-white/10 transition-colors flex items-center gap-2"
                  data-testid="lesson-ask-tutor"
                >
                  <ChatCircleDots size={14} /> Ask the AI tutor
                </button>
              </div>
            </div>
          )}

          {phase === 'quiz' && (
            <div className="space-y-6">
              <p className="text-[10px] uppercase tracking-widest text-gold">Quick check</p>
              {quiz.map((q, qi) => (
                <div key={qi} className="rounded-xl bg-white/[0.02] border border-white/10 p-4">
                  <p className="text-white font-semibold mb-3">{qi + 1}. {q.q}</p>
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => {
                      const selected = answers[qi] === oi;
                      return (
                        <button
                          key={oi}
                          onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                          className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                            selected
                              ? 'bg-gold/20 border-gold/60 text-white'
                              : 'bg-white/[0.02] border-white/10 text-zinc-300 hover:border-white/30'
                          }`}
                          data-testid={`lesson-quiz-${qi}-${oi}`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <button
                onClick={submit}
                disabled={Object.keys(answers).length !== quiz.length || submitting}
                className="w-full px-5 py-3 rounded-full bg-gradient-to-r from-yellow-300 to-yellow-500 text-black font-bold text-sm disabled:opacity-40 hover:scale-[1.02] transition-transform"
                data-testid="lesson-submit"
              >
                {submitting ? 'Submitting...' : `Submit answers (${Object.keys(answers).length}/${quiz.length})`}
              </button>
            </div>
          )}

          {phase === 'done' && result && (
            <div className="text-center py-8">
              <motion.div
                initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}
                className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-500 mb-4"
              >
                <Trophy size={40} weight="fill" className="text-black" />
              </motion.div>
              <h3 className="text-3xl font-unbounded font-bold text-white mb-2">
                {result.already_completed ? 'Revisited!' : 'Lesson complete!'}
              </h3>
              {result.xp_gained > 0 && (
                <p className="text-xl text-gold font-mono font-bold mb-2">+{result.xp_gained} XP</p>
              )}
              <div className="mt-4 space-y-2">
                <p className="text-sm text-zinc-400">Correct: {correctCount} / {quiz.length}</p>
                <p className="text-sm text-zinc-400">Streak: {result.streak_days} day{result.streak_days === 1 ? '' : 's'}</p>
              </div>
              {quiz.map((q, i) => (
                <div key={i} className="text-left mt-6 rounded-xl bg-white/[0.02] border border-white/10 p-4">
                  <p className="text-sm text-zinc-500 mb-1">{q.q}</p>
                  <p className={`text-sm font-semibold ${answers[i] === q.correct ? 'text-emerald-400' : 'text-red-400'}`}>
                    Your answer: {q.options[answers[i]]}
                  </p>
                  <p className="text-sm text-zinc-300 mt-1">{q.explanation}</p>
                </div>
              ))}
              <button
                onClick={onClose}
                className="mt-8 px-6 py-3 rounded-full bg-gradient-to-r from-yellow-300 to-yellow-500 text-black font-bold text-sm hover:scale-[1.02] transition-transform"
                data-testid="lesson-done-close"
              >
                Back to Learn
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default LessonReaderModal;
